import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import SectionLabel from '../components/SectionLabel';
import AnimatedSection from '../components/AnimatedSection';
import logoGlow from '../assets/logo-glow.png';

const orbit = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;
const orbPulse = keyframes`
  0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.8; }
  50%       { transform: translate(-50%, -50%) scale(1.12); opacity: 1; }
`;

const Section = styled.section`
  padding: 140px 80px;

  @media (max-width: 900px) {
    padding: 80px 24px;
  }
`;

const Inner = styled.div`
  max-width: 1360px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 100px;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 50px;
  }
`;

/* ─ Visual ─ */
const Visual = styled.div`
  position: relative;
  height: 520px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  @media (max-width: 900px) { height: 360px; }
  @media (max-width: 480px) { height: 280px; }
`;

const Orb = styled.div`
  position: absolute;
  border-radius: 50%;
  left: 50%; top: 50%;
  animation: ${orbPulse} ${({ $dur }) => $dur}s ease-in-out ${({ $delay }) => $delay}s infinite;
`;

const Orb1 = styled(Orb)`
  width: 320px; height: 320px;
  transform: translate(-50%, -50%);
  background: radial-gradient(circle, rgba(0,163,255,0.28) 0%, transparent 70%);
  @media (max-width: 480px) { width: 200px; height: 200px; }
`;
const Orb2 = styled.div`
  position: absolute;
  width: 200px; height: 200px;
  top: 12%; left: 5%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(200,0,255,0.18) 0%, transparent 70%);
  animation: ${orbPulse} 6s ease-in-out infinite reverse;
`;
const Orb3 = styled.div`
  position: absolute;
  width: 140px; height: 140px;
  bottom: 8%; right: 4%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(107,0,255,0.22) 0%, transparent 70%);
  animation: ${orbPulse} 5s 1s ease-in-out infinite;
`;

const Ring = styled.div`
  position: absolute;
  left: 50%; top: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  border: 1px solid rgba(0,163,255,0.1);
  animation: ${orbit} ${({ $dur }) => $dur}s linear infinite;
  ${({ $rev }) => $rev && 'animation-direction: reverse;'}

  &::before {
    content: '';
    position: absolute;
    width: 10px; height: 10px;
    background: ${({ theme }) => theme.colors.blue};
    border-radius: 50%;
    top: -5px; left: 50%;
    transform: translateX(-50%);
    box-shadow: 0 0 12px ${({ theme }) => theme.colors.blue};
  }
`;

const Ring1 = styled(Ring)`width: 210px; height: 210px; @media (max-width: 480px) { width: 140px; height: 140px; }`;
const Ring2 = styled(Ring)`width: 330px; height: 330px; @media (max-width: 480px) { width: 200px; height: 200px; }`;
const Ring3 = styled(Ring)`width: 450px; height: 450px; @media (max-width: 480px) { width: 260px; height: 260px; }`;

const CenterLogo = styled.div`
  position: absolute;
  left: 50%; top: 50%;
  transform: translate(-50%, -50%);
  width: 90px; height: 90px;
  z-index: 2;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    filter: drop-shadow(0 0 24px rgba(0,163,255,0.6));
  }
`;

/* ─ Text ─ */
const TextSide = styled.div``;

const Title = styled.h2`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(52px, 7vw, 96px);
  line-height: 0.9;
  letter-spacing: 0.02em;
  margin-bottom: 28px;

  .accent {
    background: ${({ theme }) => theme.gradients.brand};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const Body = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 44px;
`;

const List = styled.ul`
  list-style: none;
`;

const ListItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 18px;
  padding: 22px 0;
  @media (max-width: 480px) { padding: 16px 0; }
  border-bottom: 1px solid rgba(255,255,255,0.05);
  font-size: 15px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.65;

  &::before {
    content: '▸';
    color: ${({ theme }) => theme.colors.blue};
    flex-shrink: 0;
    font-size: 12px;
    margin-top: 3px;
  }

  strong {
    display: block;
    font-size: 16px;
    font-weight: 600;
    color: #fff;
    margin-bottom: 4px;
  }
`;

const HoldingLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-top: 36px;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 11px;
  letter-spacing: 0.22em;
  color: rgba(255,255,255,0.82);
  text-decoration: none;
  text-transform: uppercase;
  transition: color 0.3s;
  border-top: 1px solid rgba(255,255,255,0.06);
  padding-top: 28px;
  width: 100%;

  span { color: ${({ theme }) => theme.colors.blue}; font-weight: 700; }

  &:hover { color: #fff; }
`;

export default function Vision() {
  const { t } = useTranslation();
  const listItems = t('vision.listItems', { returnObjects: true });

  return (
    <Section id="visao">
      <Inner>
        <AnimatedSection direction="left">
          <Visual>
            <Orb1 $dur={4} $delay={0} />
            <Orb2 />
            <Orb3 />
            <Ring1 $dur={18} />
            <Ring2 $dur={28} $rev />
            <Ring3 $dur={38} />
            <CenterLogo>
              <img src={logoGlow} alt="Guebly Games" />
            </CenterLogo>
          </Visual>
        </AnimatedSection>

        <AnimatedSection direction="right">
          <TextSide>
            <SectionLabel>{t('vision.sectionLabel')}</SectionLabel>
            <Title>
              {t('vision.titleLine1')}<br />
              <span className="accent">{t('vision.titleAccent')}</span><br />
              {t('vision.titleLine3')}
            </Title>
            <Body>
              {t('vision.body')}
            </Body>

            <List>
              {listItems.map((item, i) => (
                <ListItem key={i}>
                  <div>
                    <strong>{item.title}</strong>
                    {item.desc}
                  </div>
                </ListItem>
              ))}
            </List>

            <HoldingLink href="https://www.guebly.com.br" target="_blank" rel="noopener noreferrer">
              {t('vision.holdingLink')} <span>Guebly</span> →
            </HoldingLink>
          </TextSide>
        </AnimatedSection>
      </Inner>
    </Section>
  );
}
